import React from 'react';
import Icon, { IconName } from './Icon';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'md' | 'lg';

interface ButtonProps {
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: IconName;
  /** Extra classes for the icon, e.g. "ltr:rotate-180" to point a chevron backwards. */
  iconClassName?: string;
  type?: 'button' | 'submit';
  disabled?: boolean;
  loading?: boolean;
  fullWidth?: boolean;
  className?: string;
}

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary:
    'bg-school-primary text-white shadow-[0_4px_12px_rgba(47,111,237,0.25)] hover:bg-school-primary-dark',
  secondary:
    'bg-school-surface text-school-primary border border-school-line hover:bg-school-primary-soft',
  ghost: 'bg-transparent text-school-primary hover:bg-school-primary-soft',
  danger: 'bg-school-danger text-white hover:opacity-90',
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  md: 'min-h-[40px] px-4 text-sm gap-2 rounded-xl',
  lg: 'min-h-[52px] px-6 text-base gap-2.5 rounded-2xl',
};

const Button: React.FC<ButtonProps> = ({
  children,
  onClick,
  variant = 'primary',
  size = 'md',
  icon,
  iconClassName = '',
  type = 'button',
  disabled = false,
  loading = false,
  fullWidth = false,
  className = '',
}) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled || loading}
    className={`inline-flex items-center justify-center font-semibold transition-colors touch-manipulation disabled:cursor-not-allowed disabled:opacity-50 ${VARIANT_CLASSES[variant]} ${SIZE_CLASSES[size]} ${fullWidth ? 'w-full' : ''} ${className}`}
  >
    {loading ? (
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
    ) : (
      icon && (
        <Icon name={icon} className={`${size === 'lg' ? 'w-5 h-5' : 'w-4 h-4'} ${iconClassName}`} />
      )
    )}
    <span>{children}</span>
  </button>
);

export default Button;
